import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { RoutePaths } from "../../../../../enumStrings";
import { EventFormState, EventTypeOption } from "../../eventMockData";
import { dateTimeInputPartsToUtcMilli } from "../../../../../utils/cmpTimeZones";
import {
  EventTypeLibraryHandlers,
  WizardAction,
  WizardActionButton,
  WizardStepProps,
  normalizeEventDateTimeFields,
} from "./wizardShared";
import { ResultModal } from "../../EventSales/eventSalesShared";
import { usePageHeaderOverride } from "../../../../Layout/PageContainer";
import UnsavedChangesPrompt from "../../../../common/UnsavedChangesPrompt";
import { StepProgress } from "../../../../common/dashboard/StepProgress";
import { FormPageShell } from "../../../../common/dashboard/FormPageShell";
import Step1EventDetails from "./Step1EventDetails";
import Step2DateLocation from "./Step2DateLocation";
import Step3TicketSetup from "./Step3TicketSetup";
import Step4SponsorSetup from "./Step4SponsorSetup";

// Step 4 is titled "Sponsor Set Up" everywhere; the step-4 screenshot's own
// stepper reads "Sponsors Set Up" but every other screenshot uses the
// singular, so the quirk policy normalizes on "Sponsor Set Up".
//
// footerTitle lets the footer nav labels differ from the stepper titles:
// the step-2 footer Next button binds "Ticket Set Up" — NO hyphen — per
// events-step2.md:129, and events-step3.md:222 orders the no-hyphen footer
// form reproduced verbatim (it also covers step 4's Previous label, which
// shows the real previous step name in its Q-011-corrected case).
const STEPS: {
  id: number;
  title: string;
  subtitle: string;
  footerTitle?: string;
}[] = [
  { id: 1, title: "Event Details", subtitle: "Basic information" },
  { id: 2, title: "Date & Location", subtitle: "When and where" },
  {
    id: 3,
    title: "Ticket - Set Up",
    subtitle: "Ticket types and pricing",
    footerTitle: "Ticket Set Up",
  },
  { id: 4, title: "Sponsor Set Up", subtitle: "Sponsors and packages" },
];

// Stepper items for the shared StepProgress (titles/subtitles VERBATIM from
// STEPS); the mock-bound "Your Event Planner / Coming Soon" fifth item renders
// via trailingItem (no number circle, never clickable — EVT-CREATE-001).
const stepProgressSteps = STEPS.map((stepItem) => ({
  title: stepItem.title,
  description: stepItem.subtitle,
}));

type EventWizardProps = EventTypeLibraryHandlers & {
  mode: "create" | "update";
  initialEvent: EventFormState;
  eventTypes: EventTypeOption[];
  chamberId: string;
  timeZone: string;
  saving?: boolean;
  allowStepJumpWithoutValidation?: boolean;
  onSubmit: (args: { event: EventFormState; action: WizardAction }) => void;
};

const step1ErrorKeys = new Set(["title", "eventType", "description"]);
const step2ErrorKeys = new Set([
  "startDate",
  "startTime",
  "endDate",
  "endTime",
  "format",
  "venueName",
  "address",
  "virtualAccessInstructions",
  "agendaItems",
]);
const step3ErrorKeys = new Set(["tickets"]);
const errorKeysByStep: Record<number, Set<string>> = {
  1: step1ErrorKeys,
  2: step2ErrorKeys,
  3: step3ErrorKeys,
};

// Verbatim per-step subtitles from the create screenshots (quirk-corrected;
// step 4 has no trailing period in the source).
const createStepDescriptions: Record<number, string> = {
  1: "To create an event, follow the four simple steps. Starting with Event Details.",
  2: "To create an event, follow the next step and set Date & Location.",
  3: "To create an event, follow the next steps and set Ticket - Set Up.",
  4: "To create an event, follow the next steps and set Sponsor Set Up",
};

const parseTicketPriceToCents = (value: string) => {
  const trimmedValue = value.trim();
  if (trimmedValue.startsWith("-")) return -1;
  return Math.round((Number(trimmedValue.replace(/[^0-9.]/g, "")) || 0) * 100);
};

// Rich-text fields store HTML, so "<p><br></p>" style leftovers must count
// as empty when validating required descriptions (EVT-PUBLISH-001).
const richTextToPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim();

const validateStep = (
  step: number,
  event: EventFormState,
  timeZone: string
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const normalizedEvent =
    step === 2 ? normalizeEventDateTimeFields(event) : event;
  if (step === 1) {
    if (!normalizedEvent.title.trim()) errors.title = "Event title is required.";
    if (!normalizedEvent.eventTypeId) errors.eventType = "Select an event type.";
    if (!richTextToPlainText(normalizedEvent.description)) {
      errors.description = "Brief Event Description is required.";
    }
  }
  if (step === 2) {
    if (!normalizedEvent.startDate) errors.startDate = "Start date is required.";
    if (!normalizedEvent.endDate) errors.endDate = "End date is required.";
    if (!normalizedEvent.startTime) errors.startTime = "Start time is required.";
    if (!normalizedEvent.endTime) errors.endTime = "End time is required.";
    if (!normalizedEvent.inPersonEnabled && !normalizedEvent.virtualEnabled) {
      errors.format = "Choose at least one event format.";
    }
    if (normalizedEvent.inPersonEnabled && !normalizedEvent.venueName.trim()) {
      errors.venueName = "Venue name is required for in-person events.";
    }
    if (normalizedEvent.inPersonEnabled && !normalizedEvent.address.trim()) {
      errors.address = "Venue address is required for in-person events.";
    }
    if (
      normalizedEvent.virtualEnabled &&
      !normalizedEvent.virtualAccessInstructions.trim()
    ) {
      errors.virtualAccessInstructions =
        "Virtual access details are required for virtual events.";
    }
    // Fully-empty agenda rows are already dropped by normalization; any row
    // left with only a time or only details is half-filled.
    const hasHalfFilledAgendaRow = (normalizedEvent.agendaItems || []).some(
      (agendaItem) => !agendaItem.timeLabel.trim() || !agendaItem.details.trim()
    );
    if (hasHalfFilledAgendaRow) {
      errors.agendaItems =
        "Complete both the time and the details for each agenda row, or clear the row.";
    }
    if (
      normalizedEvent.startDate &&
      normalizedEvent.startTime &&
      normalizedEvent.endDate &&
      normalizedEvent.endTime
    ) {
      const startAtUtcMilli = dateTimeInputPartsToUtcMilli(
        normalizedEvent.startDate,
        normalizedEvent.startTime,
        timeZone
      );
      const endAtUtcMilli = dateTimeInputPartsToUtcMilli(
        normalizedEvent.endDate,
        normalizedEvent.endTime,
        timeZone
      );
      if (!Number.isFinite(startAtUtcMilli) || !Number.isFinite(endAtUtcMilli)) {
        errors.startDate = "Event start and end date/time must be valid.";
      } else if (endAtUtcMilli <= startAtUtcMilli) {
        errors.endTime = "Event end date/time must be after the start date/time.";
      }
    }
  }
  if (step === 3 && !normalizedEvent.eventIsFree) {
    if (normalizedEvent.tickets.length === 0) {
      errors.tickets = "Add at least one ticket type, or mark the event free.";
    }
    normalizedEvent.tickets.forEach((ticket, index) => {
      const ticketLabel = `Ticket ${index + 1}`;
      if (!ticket.code?.trim()) {
        errors.tickets = `${ticketLabel} needs a ticket type code.`;
      } else if (!ticket.name.trim()) {
        errors.tickets = `${ticketLabel} needs a ticket type name.`;
      } else if (parseTicketPriceToCents(ticket.price) < 0) {
        errors.tickets = `${ticketLabel} price must be $0.00 or greater.`;
      } else if (
        ticket.availabilityLimit &&
        (!Number.isFinite(Number(ticket.availabilityLimit)) ||
          Number(ticket.availabilityLimit) < 0 ||
          !Number.isInteger(Number(ticket.availabilityLimit)))
      ) {
        errors.tickets = `${ticketLabel} availability must be a whole number 0 or greater.`;
      } else if (
        ticket.salesStartDate &&
        ticket.salesEndDate &&
        ticket.salesEndDate < ticket.salesStartDate
      ) {
        errors.tickets = `${ticketLabel} sales end must be after sales start.`;
      }
    });
  }
  return errors;
};

const EventWizard = ({
  mode,
  initialEvent,
  eventTypes,
  chamberId,
  timeZone,
  saving,
  allowStepJumpWithoutValidation = false,
  onSubmit,
  onAddEventType,
  onArchiveEventType,
}: EventWizardProps): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const [event, setEventState] = useState<EventFormState>(initialEvent);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scheduleInfoOpen, setScheduleInfoOpen] = useState(false);
  // Dirty-navigation guard (defect ledger SH-008/EVT-GLOBAL-002): the event
  // wizard previously lost all four steps of work on any navigation.
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(event) !== JSON.stringify(initialEvent),
    [event, initialEvent]
  );
  const activeStep = STEPS.find((stepItem) => stepItem.id === step) || STEPS[0];

  const submit = useCallback(
    (action: WizardAction) => {
      const normalizedEvent = normalizeEventDateTimeFields(event);
      const allErrors = {
        ...validateStep(1, normalizedEvent, timeZone),
        ...validateStep(2, normalizedEvent, timeZone),
        ...validateStep(3, normalizedEvent, timeZone),
      };
      setEventState(normalizedEvent);
      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        const firstStep = Object.keys(allErrors).some((key) =>
          step1ErrorKeys.has(key)
        )
          ? 1
          : Object.keys(allErrors).some((key) => step2ErrorKeys.has(key))
            ? 2
            : 3;
        setStep(firstStep);
        return;
      }
      setErrors({});
      onSubmit({ event: normalizedEvent, action });
    },
    [event, onSubmit, timeZone]
  );

  useEffect(() => {
    setPageHeaderOverride({
      // The stepper's old border-y top rule is now the shared header divider
      // (the sticky StepProgress only carries a border-b).
      withHeaderDivider: true,
      title: `${mode === "create" ? "Create Event" : "Update Event"} - ${
        activeStep.title
      }`,
      description:
        mode === "create"
          ? createStepDescriptions[step]
          : "To update an event, review and save the sections below.",
      titleClassName: "text-[34px] font-extrabold leading-tight text-black",
      descriptionClassName: "mt-3 text-lg leading-7 text-gray-500 whitespace-pre-line",
      actions: (
        <div className="flex flex-wrap items-center justify-end gap-4">
          <WizardActionButton
            tone="gold"
            startIcon={
              saving ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            disabled={saving}
            onClick={() => submit("draft")}
          >
            Save as Draft
          </WizardActionButton>
          {step === 4 ? (
            // The backend has no publish-scheduling yet (QUESTIONS.md Q-006),
            // so this explains itself instead of silently saving a draft.
            <WizardActionButton
              tone="green"
              startIcon={<CalendarMonthIcon />}
              disabled={saving}
              onClick={() => setScheduleInfoOpen(true)}
            >
              Schedule to Publish
            </WizardActionButton>
          ) : null}
        </div>
      ),
    });

    return () => setPageHeaderOverride(null);
  }, [activeStep.title, mode, saving, setPageHeaderOverride, step, submit]);

  const setEvent = (updater: (prev: EventFormState) => EventFormState) =>
    setEventState((prev) => updater(prev));

  const goToStep = (nextStep: number) => {
    const normalizedEvent = normalizeEventDateTimeFields(event);
    if (nextStep > step && !allowStepJumpWithoutValidation) {
      const stepErrors = validateStep(step, normalizedEvent, timeZone);
      setEventState(normalizedEvent);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        return;
      }
    }
    setErrors({});
    setEventState(normalizedEvent);
    setStep(Math.min(4, Math.max(1, nextStep)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visibleErrorKeys = errorKeysByStep[step];
  const stepErrorMessages = Object.entries(errors)
    .filter(([key]) => !visibleErrorKeys || visibleErrorKeys.has(key))
    .map(([, message]) => message)
    .filter((message, index, allMessages) => allMessages.indexOf(message) === index);
  const updateEventButton =
    mode === "update" ? (
      <WizardActionButton
        tone="gold"
        disabled={saving}
        onClick={() => submit("draft")}
        sx={{ minWidth: 190, minHeight: 58 }}
      >
        {saving ? "Updating..." : "Update Event"}
      </WizardActionButton>
    ) : null;

  const stepProps: WizardStepProps = {
    event,
    setEvent,
    eventTypes,
    chamberId,
    errors,
    onAddEventType,
    onArchiveEventType,
  };

  // Validation is surfaced both above the step content and near the bottom
  // actions (defect ledger SH-006) so it cannot be missed.
  const renderStepErrorAlert = (marginClassName: string) =>
    stepErrorMessages.length > 0 ? (
      <div
        className={`${marginClassName} rounded border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800`}
        role="alert"
      >
        <p className="font-extrabold">Please fix the following before continuing:</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {stepErrorMessages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <FormPageShell
      headingId={`event-wizard-step-${step}`}
      heading={activeStep.title}
      beforeMain={<UnsavedChangesPrompt when={hasUnsavedChanges && !saving} />}
      stepper={
        <StepProgress
          activeStep={step}
          ariaLabel={
            mode === "create" ? "Create event steps" : "Update event steps"
          }
          steps={stepProgressSteps}
          trailingItem={{
            title: "Your Event Planner",
            description: "Coming Soon",
          }}
          desktopGridColsClassName="lg:grid-cols-[1.1fr_1fr_1fr_1fr_1.1fr]"
          stepTestIdPrefix="event-step"
          // Update mode keeps the clickable, forward-validation-gated stepper;
          // create mode is non-clickable (matching Create Member) except for
          // the pixelQa harness, which jumps steps via
          // allowStepJumpWithoutValidation.
          onStepClick={
            mode === "update" || allowStepJumpWithoutValidation
              ? goToStep
              : undefined
          }
        />
      }
      alerts={renderStepErrorAlert("mb-8")}
      actionBarAlert={renderStepErrorAlert("mb-4")}
      contentClassName="space-y-5"
      actionBar={
        <>
          {step === 1 ? (
            // Rendering as a link keeps the shared unsaved-changes interception
            // (UnsavedChangesPrompt watches anchor clicks) in the Cancel path.
            <WizardActionButton
              tone="outline"
              component={RouterLink}
              to={RoutePaths.chamberAdmin_events_manage}
              sx={{ minWidth: 140, minHeight: 58 }}
            >
              Cancel
            </WizardActionButton>
          ) : (
            <WizardActionButton
              tone="outline"
              startIcon={<ArrowBackIcon />}
              onClick={() => goToStep(step - 1)}
              sx={{ minWidth: 220, minHeight: 58 }}
            >
              <span className="flex flex-col items-start text-left leading-tight">
                <span className="text-[13px] font-semibold">Previous:</span>
                <span className="text-[16px] font-extrabold">
                  {/* Real previous step name; footerTitle carries the
                      spec-verbatim no-hyphen "Ticket Set Up" footer form
                      (events-step3.md:222). */}
                  {STEPS[step - 2].footerTitle ?? STEPS[step - 2].title}
                </span>
              </span>
            </WizardActionButton>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            {updateEventButton}
            {step < 4 ? (
              <WizardActionButton
                tone="gold"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToStep(step + 1)}
                sx={{ minWidth: 220, minHeight: 58 }}
              >
                <span className="flex flex-col items-start text-left leading-tight">
                  <span className="text-[13px] font-semibold">Next Step:</span>
                  <span className="text-[16px] font-extrabold">
                    {/* Step 2's Next binds "Ticket Set Up" — NO hyphen — per
                        events-step2.md:129 (via footerTitle). */}
                    {STEPS[step].footerTitle ?? STEPS[step].title}
                  </span>
                </span>
              </WizardActionButton>
            ) : (
              <WizardActionButton
                tone="gold"
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <FileUploadOutlinedIcon />
                  )
                }
                onClick={() => submit("publish")}
                sx={{ minWidth: 240, minHeight: 58, fontSize: 20 }}
              >
                Publish Event
              </WizardActionButton>
            )}
          </div>
        </>
      }
    >
      {step === 1 ? <Step1EventDetails {...stepProps} /> : null}
      {step === 2 ? <Step2DateLocation {...stepProps} /> : null}
      {step === 3 ? <Step3TicketSetup {...stepProps} /> : null}
      {step === 4 ? <Step4SponsorSetup {...stepProps} /> : null}

      <ResultModal
        open={scheduleInfoOpen}
        title="Schedule to Publish is coming soon."
        message="Scheduling an event to publish on a future date is coming soon. For now, use Save as Draft, then Publish Event when you are ready to make the event live."
        onClose={() => setScheduleInfoOpen(false)}
      />
    </FormPageShell>
  );
};

export default EventWizard;
